// src/hooks/useSectorSearch.ts

import { useMemo } from "react";
import { CATEGORIES } from "@/constants/sectors/onChainPage";
import { navigation } from "@/components/layout/navbar/navbarConfig";
import type { SearchSector } from "@/types/search";

export interface UseSectorSearchResult {
  results: SearchSector[];
}

// Map navigation labels to colors and icons
const NAV_ITEM_STYLES: Record<string, { color: string; iconName: string }> = {
  "Home": { color: "indigo", iconName: "home" },
  "Market Overview": { color: "blue", iconName: "layout-dashboard" },
  "Tokens": { color: "purple", iconName: "coins" },
  "All Tokens": { color: "purple", iconName: "coins" },
  "Charts": { color: "purple", iconName: "line-chart" },
  "Movers": { color: "purple", iconName: "trending-up" },
  "Compare": { color: "purple", iconName: "git-compare" },
  "Watchlists": { color: "purple", iconName: "star" },
  "Alerts": { color: "amber", iconName: "bell" },
  "Sectors": { color: "indigo", iconName: "grid-2x2" },
  "All Sectors": { color: "indigo", iconName: "grid-2x2" },
  "Indicators": { color: "cyan", iconName: "activity" },
  "Macro": { color: "emerald", iconName: "globe" },
  "ETFs": { color: "orange", iconName: "bar-chart-3" },
  "Social & News": { color: "pink", iconName: "share-2" },
  "Twitter": { color: "sky", iconName: "twitter" },
  "YouTube": { color: "red", iconName: "youtube" },
  "News": { color: "slate", iconName: "newspaper" },
  "Investors": { color: "violet", iconName: "users" },
  "My Dashboards": { color: "teal", iconName: "layout-grid" },
  "Overview": { color: "blue", iconName: "pie-chart" },
  "Metrics": { color: "green", iconName: "bar-chart-2" },
  "Portfolio": { color: "blue", iconName: "wallet" },
  "Chat": { color: "indigo", iconName: "bot" },
  "AI Assistant": { color: "indigo", iconName: "bot" },
};

// Build a flat list of all searchable items from navigation config
const buildNavigationSectors = (): SearchSector[] => {
  const sectors: SearchSector[] = [];

  navigation.forEach((group) => {
    group.items.forEach((item) => {
      const style = NAV_ITEM_STYLES[item.label] || { color: "gray", iconName: "circle" };
      sectors.push({
        id: `nav-${item.path}`,
        title: item.label,
        description: item.description || "",
        route: item.path,
        color: style.color,
        iconName: style.iconName,
      });

      // Add children as searchable items
      if (item.children) {
        item.children.forEach((child) => {
          const childStyle = NAV_ITEM_STYLES[child.label] || style;
          sectors.push({
            id: `nav-${child.path}`,
            title: child.label,
            description: child.description || item.description || "",
            route: child.path,
            color: childStyle.color,
            iconName: childStyle.iconName,
          });
        });
      }
    });
  });

  return sectors;
};

// Build a flat list of all searchable sectors from CATEGORIES (on-chain)
const buildOnChainSectors = (): SearchSector[] => {
  const sectors: SearchSector[] = [];

  Object.entries(CATEGORIES).forEach(([id, config]) => {
    // Add main category
    sectors.push({
      id: `onchain-${id}`,
      title: config.title,
      description: config.description,
      route: `/app/${config.route}`,
      color: config.color,
      ...(config.iconName && { iconName: config.iconName }),
    });

    // Add sub-tabs as searchable items
    if (config.tabs && config.tabs.length > 0) {
      config.tabs.forEach((tab) => {
        const tabIconName = tab.iconName || config.iconName;
        sectors.push({
          id: `onchain-${id}-${tab.id}`,
          title: tab.label,
          description: tab.description || config.description,
          route: `/app/${config.route}/${tab.id}`,
          color: config.color,
          ...(tabIconName && { iconName: tabIconName }),
        });
      });
    }

    // Add dropdown items as searchable
    if (config.dropdowns) {
      Object.entries(config.dropdowns).forEach(([parentId, subTabs]) => {
        subTabs.forEach((subTab) => {
          const subTabIconName = subTab.iconName || config.iconName;
          sectors.push({
            id: `onchain-${id}-${parentId}-${subTab.id}`,
            title: subTab.label,
            description: subTab.description || config.description,
            route: `/app/${config.route}/${parentId}/${subTab.id}`,
            color: config.color,
            ...(subTabIconName && { iconName: subTabIconName }),
          });
        });
      });
    }
  });

  return sectors;
};

// Combine all searchable sectors
const buildSearchableSectors = (): SearchSector[] => {
  const navSectors = buildNavigationSectors();
  const onChainSectors = buildOnChainSectors();

  // Deduplicate by route (nav items take priority)
  const routeSet = new Set(navSectors.map(s => s.route));
  const uniqueOnChainSectors = onChainSectors.filter(s => !routeSet.has(s.route));

  return [...navSectors, ...uniqueOnChainSectors];
};

const ALL_SECTORS = buildSearchableSectors();

export function useSectorSearch(query: string): UseSectorSearchResult {
  const trimmed = query.trim().toLowerCase();

  const results = useMemo(() => {
    if (trimmed.length < 2) return [];

    return ALL_SECTORS.filter((sector) => {
      const titleMatch = sector.title.toLowerCase().includes(trimmed);
      const descMatch = sector.description.toLowerCase().includes(trimmed);
      return titleMatch || descMatch;
    }).slice(0, 5); // Limit to 5 sector results
  }, [trimmed]);

  return { results };
}
